// src/pages/sales/SalesOverviewPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Stack,
  useTheme,
  alpha,
  Link as MuiLink,
  Fade,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  NoteAdd as NoteAddIcon,
  Payments as PaymentsIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Email as EmailIcon,
  Add as AddIcon,
  Error as ErrorIcon,
  MonetizationOn as MonetizationOnIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  MoreTime as MoreTimeIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  LocalOffer as LocalOfferIcon,
  NavigateNext as NavigateNextIcon,
  DateRange as DateRangeIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  borderRadius: theme.shape.borderRadius * 1.5,
  overflow: 'hidden'
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
}));

const StyledIcon = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  width: 48,
  height: 48,
  backgroundColor: color ? theme.palette[color].lighter : theme.palette.primary.lighter,
  color: color ? theme.palette[color].main : theme.palette.primary.main,
  marginBottom: theme.spacing(2),
}));

const ShortcutButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

const IconCircle = styled(Box)(({ theme, color = 'primary' }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 60,
  height: 60,
  borderRadius: '50%',
  backgroundColor: theme.palette[color].lighter,
  color: theme.palette[color].main,
  marginBottom: theme.spacing(1),
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color = 'default';
  switch (status) {
    case 'paid':
      color = 'success';
      break;
    case 'pending':
      color = 'warning';
      break;
    case 'overdue':
      color = 'error';
      break;
    case 'draft':
      color = 'default';
      break;
    case 'sent':
      color = 'info';
      break;
    case 'cancelled':
      color = 'error';
      break;
    default:
      color = 'default';
  }
  
  return {
    fontWeight: 500,
    borderRadius: theme.shape.borderRadius,
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    backgroundColor: color === 'default' ? undefined : theme.palette[color].lighter,
    color: color === 'default' ? undefined : theme.palette[color].main,
    borderColor: color === 'default' ? undefined : theme.palette[color].main,
  };
});

const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(4), // Reduced spacing between sections
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: 12,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '@keyframes slideInUp': {
    '0%': {
      transform: 'translateY(100%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: 1,
    }
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

// Add StyledTableRow component from InvoicesPage.jsx
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    transform: 'translateX(4px)',
    transition: 'all 0.2s ease',
    boxShadow: 'inset 4px 0 0 black'
  },
  cursor: 'pointer',
  transition: 'all 0.2s ease'
}));

const CustomerOverviewPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const [companyName, setCompanyName] = useState('');

  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [outstandingBalances, setOutstandingBalances] = useState([]);
  const [customerStats, setCustomerStats] = useState({
    totalCustomers: 0,
    newThisMonth: 0,
    totalBalance: 0,
    overdueInvoices: 0,
    pendingInvoices: 0,
    invoicesPaidThisMonth: 0,
    paymentsThisMonth: 0,
    averagePaymentTime: 0,
    chartData: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('last3months');
  
  // Custom date filter state
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(dayjs().subtract(3, 'month'));
  const [customEndDate, setCustomEndDate] = useState(dayjs());
  
  // Pagination states
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [balancesPage, setBalancesPage] = useState(0);
  const [overduesPage, setOverduesPage] = useState(0);
  const rowsPerPage = 10;

  useEffect(() => {
    fetchData();
    fetchCompanyData();
  }, [companyId, dateRange, customStartDate, customEndDate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchCustomers(),
        fetchInvoices(),
        fetchOutstandingBalances()
      ]);
    } catch (error) {
      console.error('Error fetching customer data:', error);
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (error) throw error;

      if (data) {
        setCompanyName(data.name);
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  const fetchCustomers = async () => {
    try {
      const { data, error, count } = await supabase
        .from('customers')
        .select('*', { count: 'exact' })
        .eq('company_id', companyId);

      if (error) throw error;

      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      const newCustomers = data?.filter(customer => {
        const createdAt = new Date(customer.created_at);
        return createdAt.getMonth() === currentMonth && 
               createdAt.getFullYear() === currentYear;
      }).length || 0;

      setCustomers(data || []);
      setCustomerStats(prev => ({
        ...prev,
        totalCustomers: count || 0,
        newThisMonth: newCustomers
      }));
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  };

  const fetchInvoices = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          invoice_date,
          due_date,
          total_amount,
          amount_due,
          status,
          customers (id, name, email)
        `)
        .eq('company_id', companyId)
        .not('customer_id', 'is', null)
        .order('due_date');

      if (error) throw error;

      const overdue = data?.filter(invoice => {
        // Method 1: Direct status check
        if (invoice.status && invoice.status.toLowerCase() === 'overdue') {
          return true;
        }
        // Method 2: Date-based check
        return (
          (invoice.status === 'pending' || invoice.status === 'sent') && 
          invoice.due_date && 
          invoice.due_date < today
        );
      }) || [];

      const totalBalance = data?.reduce((sum, invoice) => {
        if (invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.status !== 'draft') {
          return sum + (invoice.amount_due || invoice.total_amount || 0);
        }
        return sum;
      }, 0) || 0;

      const pending = data?.filter(invoice => 
        invoice.status === 'pending' || invoice.status === 'sent'
      ).length || 0;

      const paidThisMonth = data?.filter(invoice => {
        if (invoice.status !== 'paid') return false;
        const invoiceDate = new Date(invoice.invoice_date);
        return invoiceDate.getMonth() === new Date().getMonth() &&
               invoiceDate.getFullYear() === new Date().getFullYear();
      }).length || 0;

      const recentPaidInvoices = data?.filter(invoice => invoice.status === 'paid')
        .sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date))
        .slice(0, 5) || [];

      const paymentsThisMonth = data?.filter(invoice => {
        if (invoice.status !== 'paid') return false;
        const invoiceDate = new Date(invoice.invoice_date);
        return invoiceDate.getMonth() === new Date().getMonth() &&
               invoiceDate.getFullYear() === new Date().getFullYear();
      }).reduce((sum, inv) => sum + (inv.amount_due || inv.total_amount || 0), 0) || 0;

      let chartData = [];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      if (dateRange === 'last3months') {
        for (let i = 2; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          const year = date.getFullYear();
          const month = date.getMonth();
          const monthStr = String(month + 1).padStart(2, '0');
          const monthName = monthNames[month];
          
          const monthTotal = data?.filter(invoice => {
            if (invoice.status !== 'paid' || !invoice.invoice_date) return false;
            const invoiceDate = new Date(invoice.invoice_date + 'T00:00:00');
            return invoiceDate.getFullYear() === year && invoiceDate.getMonth() === month;
          }).reduce((sum, inv) => sum + (inv.amount_due || inv.total_amount || 0), 0) || 0;
          
          chartData.push({
            day: `${monthName} '${String(year).slice(-2)}`,
            value: monthTotal,
            date: `${year}-${monthStr}`
          });
        }
      } else if (dateRange === 'last6months') {
        for (let i = 5; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          const year = date.getFullYear();
          const month = date.getMonth();
          const monthStr = String(month + 1).padStart(2, '0');
          const monthName = monthNames[month];
          
          const monthTotal = data?.filter(invoice => {
            if (invoice.status !== 'paid' || !invoice.invoice_date) return false;
            const invoiceDate = new Date(invoice.invoice_date + 'T00:00:00');
            return invoiceDate.getFullYear() === year && invoiceDate.getMonth() === month;
          }).reduce((sum, inv) => sum + (inv.amount_due || inv.total_amount || 0), 0) || 0;
          
          chartData.push({
            day: `${monthName} '${String(year).slice(-2)}`,
            value: monthTotal,
            date: `${year}-${monthStr}`
          });
        }
      } else if (dateRange === 'last9months') {
        for (let i = 8; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          const year = date.getFullYear();
          const month = date.getMonth();
          const monthStr = String(month + 1).padStart(2, '0');
          const monthName = monthNames[month];
          
          const monthTotal = data?.filter(invoice => {
            if (invoice.status !== 'paid' || !invoice.invoice_date) return false;
            const invoiceDate = new Date(invoice.invoice_date + 'T00:00:00');
            return invoiceDate.getFullYear() === year && invoiceDate.getMonth() === month;
          }).reduce((sum, inv) => sum + (inv.amount_due || inv.total_amount || 0), 0) || 0;
          
          chartData.push({
            day: `${monthName} '${String(year).slice(-2)}`,
            value: monthTotal,
            date: `${year}-${monthStr}`
          });
        }
      } else if (dateRange === 'lastyear') {
        for (let i = 11; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          const year = date.getFullYear();
          const month = date.getMonth();
          const monthStr = String(month + 1).padStart(2, '0');
          const monthName = monthNames[month];
          
          const monthTotal = data?.filter(invoice => {
            if (invoice.status !== 'paid' || !invoice.invoice_date) return false;
            const invoiceDate = new Date(invoice.invoice_date + 'T00:00:00');
            return invoiceDate.getFullYear() === year && invoiceDate.getMonth() === month;
          }).reduce((sum, inv) => sum + (inv.amount_due || inv.total_amount || 0), 0) || 0;
          
          chartData.push({
            day: `${monthName} '${String(year).slice(-2)}`,
            value: monthTotal,
            date: `${year}-${monthStr}`
          });
        }
      } else if (dateRange === 'thisyear') {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        
        for (let month = 0; month <= currentMonth; month++) {
          const monthName = monthNames[month];
          const monthStr = String(month + 1).padStart(2, '0');
          
          const monthTotal = data?.filter(invoice => {
            if (invoice.status !== 'paid' || !invoice.invoice_date) return false;
            const invoiceDate = new Date(invoice.invoice_date + 'T00:00:00');
            return invoiceDate.getFullYear() === currentYear && invoiceDate.getMonth() === month;
          }).reduce((sum, inv) => sum + (inv.total_amount || 0), 0) || 0;
          
          chartData.push({
            day: `${monthName} '${String(currentYear).slice(-2)}`,
            value: monthTotal,
            date: `${currentYear}-${monthStr}`
          });
        }
      } else if (dateRange === 'custom') {
        const startDate = customStartDate.startOf('month');
        const endDate = customEndDate.endOf('month');
        let currentDate = startDate.clone();
        
        while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'month')) {
          const year = currentDate.year();
          const month = currentDate.month();
          const monthName = monthNames[month];
          const monthStr = String(month + 1).padStart(2, '0');
          
          const monthTotal = data?.filter(invoice => {
            if (invoice.status !== 'paid' || !invoice.invoice_date) return false;
            const invoiceDate = new Date(invoice.invoice_date + 'T00:00:00');
            return invoiceDate.getFullYear() === year && invoiceDate.getMonth() === month;
          }).reduce((sum, inv) => sum + (inv.amount_due || inv.total_amount || 0), 0) || 0;
          
          chartData.push({
            day: `${monthName} '${String(year).slice(-2)}`,
            value: monthTotal,
            date: `${year}-${monthStr}`
          });
          
          currentDate = currentDate.add(1, 'month');
        }
      }

      setInvoices(overdue);
      setRecentPayments(recentPaidInvoices);
      setCustomerStats(prev => ({
        ...prev,
        totalBalance: totalBalance,
        overdueInvoices: overdue.length,
        pendingInvoices: pending,
        invoicesPaidThisMonth: paidThisMonth,
        paymentsThisMonth: paymentsThisMonth,
        averagePaymentTime: 0,
        chartData: chartData
      }));
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  };

  const fetchOutstandingBalances = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          customer_id,
          total_amount,
          amount_due,
          status,
          due_date,
          invoice_date,
          customers (id, name, email)
        `)
        .eq('company_id', companyId)
        .not('customer_id', 'is', null)
        .in('status', ['pending', 'sent']);

      if (error) throw error;

      const nonOverdueInvoices = data?.filter(invoice => {
        if (!invoice.due_date) return true;
        return invoice.due_date >= today;
      }).sort((a, b) => ((b.amount_due || b.total_amount) || 0) - ((a.amount_due || a.total_amount) || 0)) || [];

      setOutstandingBalances(nonOverdueInvoices);
    } catch (error) {
      console.error('Error fetching outstanding balances:', error);
      throw error;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const getDaysOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Permission check - Sales Managers and Sales Reps can access
  if (!hasPermission('sales.overview.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{ 
            p: 8, 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <MonetizationOnIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              CRM Overview Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the CRM Overview. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#2196F3', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#2196F3', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#2196F3', 0.1),
                mb: 2
              }}>
                <MonetizationOnIcon sx={{ fontSize: 48, color: '#2196F3' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#2196F3',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading CRM Overview
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing customer relationships and sales performance...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#2196F3',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Container 
          maxWidth="xl"
          sx={{
            py: 3,
            pb: 8,
            px: { xs: 2, sm: 3 },
            width: '100%',
            maxWidth: '100vw !important'
          }}
        >
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, borderRadius: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

              {/* Enhanced Page Header */}
              <Card sx={{
                borderRadius: 3,
                mt: 5,
                mb: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: theme.palette.grey[100],
                backdropFilter: 'blur(20px)',
                boxShadow: 'none',
                borderTop: '3px solid #2196F3',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${alpha('#2196F3', 0.15)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <Box sx={{
                      p: 2,
                      borderRadius: 3,
                      background: alpha('#2196F3', 0.08),
                      border: `1px solid ${alpha('#2196F3', 0.15)}`,
                    }}>
                      <GroupsIcon sx={{ fontSize: 32, color: '#2196F3' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{
              fontWeight: 500,
              color: '#2196F3',
              mb: 0.5,
              fontSize: '2rem',
              letterSpacing: '-0.5px'
            }}>
              CRM Overview
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary', 
              fontWeight: 500,
              fontSize: '1rem'
            }}>
              {companyName || 'Loading...'}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ 
          color: 'text.secondary', 
          maxWidth: 700, 
          lineHeight: 1.5,
          fontSize: '0.95rem',
                    fontWeight: 500
        }}>
          Manage customer relationships, track payments, and monitor account balances across your business.
        </Typography>
                </CardContent>
              </Card>
            </Box>

      {/* Stats Cards */}
      <Box sx={{ 
        mb: 3, 
        animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '@keyframes slideInUp': {
          '0%': {
            transform: 'translateY(100%)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateY(0)',
            opacity: 1,
          }
        }
      }}>
        <Grid container spacing={3} sx={{ maxWidth: '100%', width: '100%' }}>
          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                border: `1px solid ${theme.palette.grey[400]}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'primary.main', 
                      mb: 1,
                      fontSize: '2rem',
                      lineHeight: 1
                    }}>
                      {customerStats.totalCustomers}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Total Customers
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}>
                      +{customerStats.newThisMonth} this month
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 3, 
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.02)})`,
                border: `1px solid ${theme.palette.warning.main}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.warning.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'warning.main', 
                      mb: 1,
                      fontSize: '1.5rem',
                      lineHeight: 1
                    }}>
                      {formatCurrency(customerStats.totalBalance)}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Outstanding Balance
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}>
                      {customerStats.pendingInvoices} pending, {customerStats.overdueInvoices} overdue
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 3, 
                      background: theme.palette.warning.main,
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha(theme.palette.warning.main, 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <MonetizationOnIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.02)})`,
                border: `1px solid ${theme.palette.success.main}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.success.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'success.main', 
                      mb: 1,
                      fontSize: '1.5rem',
                      lineHeight: 1
                    }}>
                      {formatCurrency(customerStats.paymentsThisMonth)}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Payments This Month
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}>
                      {customerStats.invoicesPaidThisMonth} invoices paid
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 3, 
                      background: theme.palette.success.main,
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha(theme.palette.success.main, 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
                border: `1px solid ${theme.palette.info.main}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.info.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'info.main', 
                      mb: 1,
                      fontSize: '2rem',
                      lineHeight: 1
                    }}>
                      {customerStats.averagePaymentTime > 0 ? `${customerStats.averagePaymentTime}d` : 'On Time'}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Avg Payment Time
                    </Typography>
                    <Typography variant="body2" color={customerStats.averagePaymentTime > 0 ? 'warning.main' : 'success.main'} sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}>
                      {customerStats.averagePaymentTime > 0 ? 'Past due date' : 'Before due date'}
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 3, 
                      background: theme.palette.info.main,
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha(theme.palette.info.main, 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <AccountBalanceIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Quick Actions Section */}
      <SectionPaper>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box 
              sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
              }}
            >
              <AssessmentIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
              Quick Actions
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
            Common sales operations and management tasks
          </Typography>
        </Box>
        
        <Grid container spacing={3}>
          {/* View Actions */}
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              onClick={() => navigate(`/company/${companyId}/customers-list`)}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                width: '100%',
                height: '100%',
                '&:hover': { 
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box 
                  sx={{ 
                    p: 1, 
                    borderRadius: '50%', 
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: 'info.main',
                    flexShrink: 0
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.25, fontSize: '0.95rem' }}>
                    View Customers
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    Browse all customers
                  </Typography>
                </Box>
              </Box>
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              onClick={() => navigate(`/company/${companyId}/invoices`)}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.02)})`,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                width: '100%',
                height: '100%',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: '50%', 
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: 'warning.main',
                    flexShrink: 0
                  }}
                >
                  <ReceiptIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    View Invoices
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All invoices
                  </Typography>
                </Box>
              </Box>
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              onClick={() => navigate(`/company/${companyId}/price-lists`)}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.02)})`,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                width: '100%',
                height: '100%',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: '50%', 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                    flexShrink: 0
                  }}
                >
                  <LocalOfferIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    View Price Lists
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Browse all price lists
                  </Typography>
                </Box>
              </Box>
            </Button>
          </Grid>

          {/* Create Actions */}
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              onClick={() => navigate(`/company/${companyId}/customer-new`)}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                width: '100%',
                height: '100%',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: '50%', 
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    flexShrink: 0
                  }}
                >
                  <AddIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    New Customer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add new customer
                  </Typography>
                </Box>
              </Box>
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              onClick={() => navigate(`/company/${companyId}/invoices/new`)}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.02)})`,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                width: '100%',
                height: '100%',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: '50%', 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                    flexShrink: 0
                  }}
                >
                  <NoteAddIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Create Invoice
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New invoice
                  </Typography>
                </Box>
              </Box>
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              onClick={() => navigate(`/company/${companyId}/price-lists?create=true`)}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                width: '100%',
                height: '100%',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: '50%', 
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: 'info.main',
                    flexShrink: 0
                  }}
                >
                  <AddIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Create Price List
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New price list
                  </Typography>
                </Box>
              </Box>
            </Button>
          </Grid>
        </Grid>
      </SectionPaper>

      {/* Sales Chart Section */}
      <SectionPaper>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.main})`,
                  color: 'white',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.25)}`
                }}
              >
                <TrendingUpIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
                Revenue Overview
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label="Last 3 Months" 
                size="small" 
                color={dateRange === 'last3months' ? 'primary' : 'default'}
                variant={dateRange === 'last3months' ? 'filled' : 'outlined'}
                onClick={() => setDateRange('last3months')}
                sx={{ 
                  cursor: 'pointer',
                  borderRadius: 2,
                  fontWeight: 500,
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
              <Chip 
                label="Last 6 Months" 
                size="small" 
                color={dateRange === 'last6months' ? 'primary' : 'default'}
                variant={dateRange === 'last6months' ? 'filled' : 'outlined'}
                onClick={() => setDateRange('last6months')}
                sx={{ 
                  cursor: 'pointer',
                  borderRadius: 2,
                  fontWeight: 500,
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
              <Chip 
                label="Last 9 Months" 
                size="small" 
                color={dateRange === 'last9months' ? 'primary' : 'default'}
                variant={dateRange === 'last9months' ? 'filled' : 'outlined'}
                onClick={() => setDateRange('last9months')}
                sx={{ 
                  cursor: 'pointer',
                  borderRadius: 2,
                  fontWeight: 500,
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
              <Chip 
                label="Last Year" 
                size="small" 
                color={dateRange === 'lastyear' ? 'primary' : 'default'}
                variant={dateRange === 'lastyear' ? 'filled' : 'outlined'}
                onClick={() => setDateRange('lastyear')}
                sx={{ 
                  cursor: 'pointer',
                  borderRadius: 2,
                  fontWeight: 500,
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
              <Chip 
                label="This Year" 
                size="small" 
                color={dateRange === 'thisyear' ? 'primary' : 'default'}
                variant={dateRange === 'thisyear' ? 'filled' : 'outlined'}
                onClick={() => setDateRange('thisyear')}
                sx={{ 
                  cursor: 'pointer',
                  borderRadius: 2,
                  fontWeight: 500,
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
              <Chip 
                label="Custom" 
                size="small" 
                icon={<DateRangeIcon />}
                color={dateRange === 'custom' ? 'primary' : 'default'}
                variant={dateRange === 'custom' ? 'filled' : 'outlined'}
                onClick={() => setCustomDateDialogOpen(true)}
                sx={{ 
                  cursor: 'pointer',
                  borderRadius: 2,
                  fontWeight: 500,
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
        
        <Box>
          {(() => {
            const chartData = customerStats.chartData || [];
            const maxValue = Math.max(...chartData.map(d => d.value), 100);
            const yAxisMax = Math.ceil(maxValue / 50) * 50;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            
            return (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500, color: 'text.primary', fontSize: '1.1rem' }}>
                  Revenue Trend ({
                    dateRange === 'last3months' ? 'Last 3 Months' : 
                    dateRange === 'last6months' ? 'Last 6 Months' : 
                    dateRange === 'last9months' ? 'Last 9 Months' :
                    dateRange === 'lastyear' ? 'Last Year' :
                    dateRange === 'thisyear' ? 'This Year' :
                    dateRange === 'custom' ? `${customStartDate.format('MMM YYYY')} - ${customEndDate.format('MMM YYYY')}` :
                    'Last Year'
                  })
                </Typography>
                
                {chartData.length === 0 || chartData.every(d => d.value === 0) ? (
                  <Box sx={{ 
                    height: 400, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: 'text.secondary',
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: 3,
                    background: alpha(theme.palette.grey[100], 0.3)
                  }}>
                    <TrendingDownIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                      No Sales Data
                    </Typography>
                    <Typography variant="body2">
                      No sales recorded for {
                        dateRange === 'last3months' ? 'the last 3 months' : 
                        dateRange === 'last6months' ? 'the last 6 months' : 
                        dateRange === 'last9months' ? 'the last 9 months' :
                        dateRange === 'lastyear' ? 'the last year' :
                        dateRange === 'thisyear' ? 'this year' :
                        dateRange === 'custom' ? 'the selected period' :
                        'the last year'
                      }
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ 
                    height: 350,
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    p: 2,
                    position: 'relative'
                  }}>
                    {/* Chart Container */}
                    <Box sx={{ 
                      height: '100%',
                      display: 'flex',
                      position: 'relative'
                    }}>
                      {/* Y-Axis */}
                      <Box sx={{ 
                        width: 80,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        pr: 2,
                        pb: 4
                      }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#333' }}>
                          ${yAxisMax}
                        </Typography>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#333' }}>
                          ${Math.round(yAxisMax * 0.75)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#333' }}>
                          ${Math.round(yAxisMax * 0.5)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#333' }}>
                          ${Math.round(yAxisMax * 0.25)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#333' }}>
                          $0
                        </Typography>
                      </Box>

                      {/* Chart Area */}
                      <Box sx={{ 
                        flex: 1,
                        height: '100%',
                        position: 'relative',
                        pb: 4
                      }}>
                        {/* Grid Lines */}
                        <Box sx={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 32,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          zIndex: 1
                        }}>
                          {Array.from({ length: 5 }, (_, i) => (
                            <Box key={i} sx={{ 
                              height: '1px', 
                              bgcolor: alpha(theme.palette.grey[300], 0.6),
                              width: '100%'
                            }} />
                          ))}
                        </Box>

                        {/* Bars Container */}
                        <Box sx={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 32,
                          display: 'grid',
                          gridTemplateColumns: `repeat(${chartData.length}, 1fr)`,
                          gap: 1,
                          alignItems: 'end',
                          zIndex: 2
                        }}>
                          {chartData.map((item, index) => {
                            const heightPercentage = yAxisMax > 0 ? (item.value / yAxisMax) * 100 : 0;
                            const isToday = item.date === todayStr;
                            
                            return (
                              <Tooltip 
                                key={index}
                                title={`${item.day}: ${formatCurrency(item.value)}`} 
                                placement="top"
                                arrow
                              >
                                <Box sx={{ 
                                  display: 'flex',
                                  justifyContent: 'center',
                                  height: '100%',
                                  alignItems: 'end'
                                }}>
                                  <Box sx={{
                                    width: '70%',
                                    maxWidth: 60,
                                    height: `${heightPercentage}%`,
                                    minHeight: item.value > 0 ? '4px' : '0px',
                                    backgroundColor: item.value > 0 ? '#4CAF50' : '#BDBDBD',
                                    borderRadius: '4px 4px 0 0',
                                    cursor: 'pointer'
                                  }} />
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>

                        {/* Labels */}
                        <Box sx={{ 
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 32,
                          display: 'grid',
                          gridTemplateColumns: `repeat(${chartData.length}, 1fr)`,
                          gap: 1,
                          alignItems: 'center'
                        }}>
                          {chartData.map((item, index) => {
                            const isToday = item.date === todayStr;
                            return (
                              <Typography 
                                key={index}
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: isToday ? 700 : 500,
                                  color: isToday ? theme.palette.warning.main : '#666',
                                  textAlign: 'center'
                                }}
                              >
                                {item.day}
                              </Typography>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Box>
      </SectionPaper>

      {/* Recent Payments Section */}
      <SectionPaper>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box 
              sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                color: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.25)}`
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.dark', fontSize: '1.25rem' }}>
                Recent Payments
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.05rem', fontWeight: 500 }}>
                Latest customer payments and cash flow activity
              </Typography>
            </Box>
          </Box>
        </Box>

        {recentPayments.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center',
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 3,
            background: alpha(theme.palette.success.main, 0.05)
          }}>
            <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1">
              No recent payments found
            </Typography>
          </Box>
        ) : (
          <>
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '@keyframes slideInUp': {
                '0%': {
                  transform: 'translateY(100%)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'translateY(0)',
                  opacity: 1,
                }
              }
            }}>
              <TableContainer sx={{ overflow: 'visible' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Invoice #
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Customer
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Payment Date
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Amount
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentPayments
                      .slice(paymentsPage * rowsPerPage, paymentsPage * rowsPerPage + rowsPerPage)
                      .map((payment) => (
                      <StyledTableRow 
                        key={payment.id} 
                        hover 
                        onClick={() => navigate(`/company/${companyId}/invoices/edit/${payment.id}`)}
                      >
                        <TableCell sx={{ 
                          py: 2.5, 
                          px: 3,
                          fontSize: '0.95rem',
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ReceiptIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.95rem' }}>
                              {payment.invoice_number}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2.5, 
                          px: 3,
                          fontSize: '0.9rem',
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.9rem' }}>
                            {payment.customers?.name || 'Unnamed Customer'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2.5, 
                          px: 3,
                          fontSize: '0.9rem',
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.primary' }}>
                              {formatDate(payment.invoice_date)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          py: 2.5, 
                          px: 3,
                          fontSize: '0.9rem',
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main', fontSize: '0.9rem' }}>
                            +{formatCurrency(payment.amount_due || payment.total_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2.5, 
                          px: 3,
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <StatusChip 
                            label="Paid" 
                            status="paid"
                            size="small"
                            sx={{
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.75rem',
                              borderRadius: 2
                            }}
                          />
                        </TableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ 
                borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderRadius: '0 0 12px 12px'
              }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={recentPayments.length}
                  rowsPerPage={rowsPerPage}
                  page={paymentsPage}
                  onPageChange={(event, newPage) => setPaymentsPage(newPage)}
                  onRowsPerPageChange={() => {}}
                  sx={{ 
                    px: 3,
                    py: 2,
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      color: theme.palette.text.primary
                    },
                    '& .MuiTablePagination-select': {
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      background: 'white',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    },
                    '& .MuiTablePagination-actions': {
                      '& .MuiIconButton-root': {
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        borderRadius: 2,
                        margin: '0 2px',
                        background: 'white',
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: theme.palette.primary.main,
                          color: 'white',
                          transform: 'translateY(-1px)',
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        },
                        '&.Mui-disabled': {
                          background: alpha(theme.palette.grey[300], 0.5),
                          color: theme.palette.grey[500],
                          border: `1px solid ${theme.palette.grey[300]}`,
                          transform: 'none',
                          boxShadow: 'none'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </>
        )}
      </SectionPaper>

      {/* Outstanding Balances Section */}
      <SectionPaper>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box 
              sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                color: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.25)}`
              }}
            >
              <MonetizationOnIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'warning.dark', fontSize: '1.25rem' }}>
                Outstanding Invoices
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.05rem', fontWeight: 500 }}>
                {outstandingBalances.length} pending invoices totaling {formatCurrency(outstandingBalances.reduce((total, invoice) => total + (invoice.amount_due || invoice.total_amount || 0), 0))}
              </Typography>
            </Box>
          </Box>
        </Box>

        {outstandingBalances.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center',
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 3,
            background: alpha(theme.palette.success.main, 0.05)
          }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="body1">
              All invoices are paid up. Great job!
            </Typography>
          </Box>
        ) : (
          <>
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '@keyframes slideInUp': {
                '0%': {
                  transform: 'translateY(100%)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'translateY(0)',
                  opacity: 1,
                }
              }
            }}>
              <TableContainer sx={{ overflow: 'visible' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Invoice #
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Customer
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Due Date
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Amount
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3,
                          width: 160
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outstandingBalances
                      .slice(balancesPage * rowsPerPage, balancesPage * rowsPerPage + rowsPerPage)
                      .map((invoice) => (
                      <StyledTableRow 
                        key={invoice.id} 
                        hover 
                        onClick={() => navigate(`/company/${companyId}/invoices/edit/${invoice.id}`)}
                      >
                        <TableCell sx={{ 
                          py: 2.5, 
                          px: 3,
                          fontSize: '0.95rem',
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ReceiptIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.95rem' }}>
                              {invoice.invoice_number}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2.5, 
                          px: 3,
                          fontSize: '0.9rem',
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.9rem' }}>
                              {invoice.customers?.name || 'Unnamed Customer'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2.5, 
                          px: 3,
                          fontSize: '0.9rem',
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.primary' }}>
                            {formatDate(invoice.due_date)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          py: 2.5, 
                          px: 3,
                          fontSize: '0.9rem',
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'warning.main', fontSize: '0.9rem' }}>
                            {formatCurrency(invoice.amount_due || invoice.total_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2.5, 
                          px: 3,
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <StatusChip 
                            label={invoice.status}
                            status={invoice.status}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.75rem',
                              borderRadius: 2
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          py: 2.5, 
                          px: 3,
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                        }}>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/company/${companyId}/invoices/edit/${invoice.id}`);
                            }}
                            sx={{ 
                              borderRadius: 2,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              px: 2,
                              py: 1,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              '&:hover': {
                                bgcolor: 'primary.main',
                                color: 'white',
                                transform: 'scale(1.05)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                              }
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ 
                borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderRadius: '0 0 12px 12px'
              }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={outstandingBalances.length}
                  rowsPerPage={rowsPerPage}
                  page={balancesPage}
                  onPageChange={(event, newPage) => setBalancesPage(newPage)}
                  onRowsPerPageChange={() => {}}
                  sx={{ 
                    px: 3,
                    py: 2,
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      color: theme.palette.text.primary
                    },
                    '& .MuiTablePagination-select': {
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      background: 'white',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    },
                    '& .MuiTablePagination-actions': {
                      '& .MuiIconButton-root': {
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        borderRadius: 2,
                        margin: '0 2px',
                        background: 'white',
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: theme.palette.primary.main,
                          color: 'white',
                          transform: 'translateY(-1px)',
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        },
                        '&.Mui-disabled': {
                          background: alpha(theme.palette.grey[300], 0.5),
                          color: theme.palette.grey[500],
                          border: `1px solid ${theme.palette.grey[300]}`,
                          transform: 'none',
                          boxShadow: 'none'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </>
        )}

        <Box sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider', 
          display: 'flex', 
          justifyContent: 'flex-end',
          bgcolor: alpha(theme.palette.grey[50], 0.3)
        }}>
          <Button 
            size="small" 
            onClick={() => navigate(`/company/${companyId}/invoices`)}
            sx={{ 
              fontSize: '0.75rem',
              fontWeight: 500,
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
          >
            View All Invoices
          </Button>
        </Box>
      </SectionPaper>

      {/* Overdue Invoices Section */}
      <SectionPaper>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box 
              sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                color: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.25)}`
              }}
            >
              <MoreTimeIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'error.dark', fontSize: '1.25rem' }}>
                Overdue Invoices
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.05rem', fontWeight: 500 }}>
                Total of {customerStats.overdueInvoices} overdue {customerStats.overdueInvoices === 1 ? 'invoice' : 'invoices'} worth {formatCurrency(invoices.reduce((total, inv) => total + (inv.amount_due || inv.total_amount || 0), 0))}
              </Typography>
            </Box>
          </Box>
        </Box>

        {invoices.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center',
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 3,
            background: alpha(theme.palette.success.main, 0.05)
          }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="body1">
              No overdue invoices found. Great job!
            </Typography>
          </Box>
        ) : (
          <>
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '@keyframes slideInUp': {
                '0%': {
                  transform: 'translateY(100%)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'translateY(0)',
                  opacity: 1,
                }
              }
            }}>
              <TableContainer sx={{ overflow: 'visible' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Invoice #
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Customer
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Due Date
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Days Overdue
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Amount
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3,
                          width: 160
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices
                      .slice(overduesPage * rowsPerPage, overduesPage * rowsPerPage + rowsPerPage)
                      .map((invoice) => {
                      const daysOverdue = getDaysOverdue(invoice.due_date);
                      return (
                        <StyledTableRow 
                          key={invoice.id} 
                          hover 
                          onClick={() => navigate(`/company/${companyId}/invoices/edit/${invoice.id}`)}
                        >
                          <TableCell sx={{ 
                            py: 2.5, 
                            px: 3,
                            fontSize: '0.95rem',
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ReceiptIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                              <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.95rem' }}>
                                {invoice.invoice_number}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ 
                            py: 2.5, 
                            px: 3,
                            fontSize: '0.9rem',
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.9rem' }}>
                              {invoice.customers?.name || 'Unnamed Customer'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ 
                            py: 2.5, 
                            px: 3,
                            fontSize: '0.9rem',
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ErrorIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.primary' }}>
                                {formatDate(invoice.due_date)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ 
                            py: 2.5, 
                            px: 3,
                            fontSize: '0.9rem',
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main', fontSize: '0.9rem' }}>
                              {daysOverdue} {daysOverdue === 1 ? 'day' : 'days'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            py: 2.5, 
                            px: 3,
                            fontSize: '0.9rem',
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.primary' }}>
                              {formatCurrency(invoice.amount_due || invoice.total_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ 
                            py: 2.5, 
                            px: 3,
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                          }}>
                            <StatusChip 
                              label={invoice.status === 'pending' ? 'Overdue' : invoice.status} 
                              status={invoice.status === 'pending' ? 'overdue' : invoice.status}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                fontSize: '0.75rem',
                                borderRadius: 2
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            py: 2.5, 
                            px: 3,
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
                          }}>
                            <Button 
                              size="small" 
                              variant="outlined" 
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/company/${companyId}/invoices/edit/${invoice.id}`);
                              }}
                              sx={{ 
                                borderRadius: 2,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                px: 2,
                                py: 1,
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                '&:hover': {
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  transform: 'scale(1.05)',
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                }
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ 
                borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderRadius: '0 0 12px 12px'
              }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={invoices.length}
                  rowsPerPage={rowsPerPage}
                  page={overduesPage}
                  onPageChange={(event, newPage) => setOverduesPage(newPage)}
                  onRowsPerPageChange={() => {}}
                  sx={{ 
                    px: 3,
                    py: 2,
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      color: theme.palette.text.primary
                    },
                    '& .MuiTablePagination-select': {
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      background: 'white',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    },
                    '& .MuiTablePagination-actions': {
                      '& .MuiIconButton-root': {
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        borderRadius: 2,
                        margin: '0 2px',
                        background: 'white',
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: theme.palette.primary.main,
                          color: 'white',
                          transform: 'translateY(-1px)',
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        },
                        '&.Mui-disabled': {
                          background: alpha(theme.palette.grey[300], 0.5),
                          color: theme.palette.grey[500],
                          border: `1px solid ${theme.palette.grey[300]}`,
                          transform: 'none',
                          boxShadow: 'none'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </>
        )}

        <Box sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider', 
          display: 'flex', 
          justifyContent: 'flex-end',
          bgcolor: alpha(theme.palette.grey[50], 0.3)
        }}>
          <Button 
            size="small" 
            onClick={() => navigate(`/company/${companyId}/invoices`)}
            sx={{ 
              fontSize: '0.75rem',
              fontWeight: 500,
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
          >
            View All Invoices
          </Button>
        </Box>
      </SectionPaper>

      {/* Bottom spacing */}
      <Box sx={{ pb: 4 }} />

      {/* Custom Date Range Dialog */}
      <Dialog
        open={customDateDialogOpen}
        onClose={() => setCustomDateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.2)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          background: theme.palette.primary.main,
          color: 'white',
          p: 3,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <DateRangeIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Select Custom Date Range
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4, pt: 8, pb: 8 }}>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 3 }}>
                <DatePicker
                  label="Start Date"
                  value={customStartDate}
                  onChange={(newValue) => setCustomStartDate(newValue)}
                  views={['year', 'month']}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    }
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 3 }}>
                <DatePicker
                  label="End Date"
                  value={customEndDate}
                  onChange={(newValue) => setCustomEndDate(newValue)}
                  views={['year', 'month']}
                  minDate={customStartDate}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{
          p: 3,
          background: alpha(theme.palette.grey[50], 0.8),
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
        }}>
          <Button
            onClick={() => setCustomDateDialogOpen(false)}
            sx={{
              borderRadius: 3,
              fontSize: '0.9rem',
              fontWeight: 500,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              color: 'text.primary',
              px: 3,
              py: 1.5,
              '&:hover': {
                bgcolor: alpha(theme.palette.grey[100], 0.8),
                border: `1px solid ${theme.palette.primary.main}`,
                transform: 'translateY(-1px)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setDateRange('custom');
              setCustomDateDialogOpen(false);
            }}
            variant="contained"
            sx={{
              borderRadius: 3,
              fontSize: '0.9rem',
              fontWeight: 500,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              px: 3,
              py: 1.5,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              '&:hover': {
                transform: 'translateY(-2px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}
          >
            Apply Range
          </Button>
        </DialogActions>
      </Dialog>

          </Container>
      </Fade>
    </LocalizationProvider>
  );
};

export default CustomerOverviewPage;